import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import PropTypes from 'prop-types';
import React from 'react';
import Token from './Token.js';
import { p as props, d as defaults } from './componentEl-33ff2aeb.js';
import { a as addClassNames, c as classNameProps } from './classNames-6a4763fd.js';
import { a as addSpacingClasses, m as marginProps } from './spacing-d13ff368.js';
import './Icon.js';
import './Avatar.js';
import './_variables.es6-3122d3a2.js';

/*
Generic component for a single toggle item, to be used in the context of Tab
*/

function TabItem(_ref) {
  let {
    componentEl: Component,
    onChange,
    className,
    count,
    icon,
    iconColor,
    selected,
    children,
    disabled,
    value
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["componentEl", "onChange", "className", "count", "icon", "iconColor", "selected", "children", "disabled", "value"]);

  function localChangeHandler() {
    if (!selected && !disabled) onChange(value);
  }

  const cssClasses = ['st_tab'];
  if (className) cssClasses.push(className);
  const componentProps = {};
  if (selected) cssClasses.push('st_tab--selected');

  if (disabled) {
    componentProps['aria-disabled'] = 'true';
    cssClasses.push('st_tab--disabled');
    if (Component === 'button') componentProps.disabled = true;
  }

  if (Component === 'button') componentProps.disabled = selected;
  return /*#__PURE__*/React.createElement(Component, _extends({
    onClick: localChangeHandler,
    className: cssClasses.join(' '),
    role: "tab"
  }, componentProps, otherProps), children, icon && /*#__PURE__*/React.createElement(Token, {
    icon: icon,
    margin: {
      left: 'sm'
    },
    color: iconColor
  }), typeof count === 'number' && !Number.isNaN(count) && /*#__PURE__*/React.createElement(Token, {
    margin: {
      left: 'sm'
    },
    color: selected ? 'blurple' : 'gray'
  }, count));
}

TabItem.propTypes = {
  /** additional CSS classes for outermost element, if any */
  className: PropTypes.string,

  /** callback when tab is clicked, and is not currently selected */
  onChange: PropTypes.func,

  /** component or DOM element to use for rendering outermost tag */
  componentEl: props,

  /* optional number */
  count: PropTypes.number,

  /** optional icon */
  icon: PropTypes.string,

  /** optional icon color */
  iconColor: PropTypes.oneOf(['gray', 'lava', 'blurple', 'emerald', 'tangerine']),

  /** whether tab is currently selected */
  selected: PropTypes.bool,

  /** value of tab, useful when tab is clicked */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** whether control is disabled */
  disabled: PropTypes.bool
}; // default props

TabItem.defaultProps = {
  className: null,
  onChange: null,
  count: null,
  componentEl: 'button',
  icon: null,
  iconColor: 'gray',
  selected: false,
  value: undefined,
  disabled: false
};

/*
Generic component for Tabs
Accepts one or more Tab.Item component inside
Does not do any state management! See Storybook for examples for state management
*/

function Tabs(_ref) {
  let {
    componentEl: Component,
    className,
    onChange,
    children,
    margin,
    value
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["componentEl", "className", "onChange", "children", "margin", "value"]);

  let childIndex = 0;
  const childrenWithProps = React.Children.map(children, child => {
    if (!React.isValidElement(child)) {
      return null;
    }

    const childValue = typeof child.props.value === 'undefined' ? childIndex : child.props.value;
    const selected = childValue === value;
    childIndex += 1;
    return React.cloneElement(child, {
      selected,
      onChange,
      value: childValue
    });
  });
  let cssClasses = ['st_tabs'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  }); // Role's of tablist, tab, etc. should only be added if the tabs correspond to panels
  //   - https://getbootstrap.com/docs/5.0/components/navs-tabs/#regarding-accessibility
  //   - So, it should not be added unless a full-blown tabs with tab panels component is built

  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), childrenWithProps);
} // proptypes definitions


Tabs.propTypes = {
  /** event handler when tab is clicked */
  onChange: PropTypes.func,

  /** extra CSS classes, if any */
  className: classNameProps,

  /** dom element or component to use for outermost wrapper */
  componentEl: props,

  /** margin for component, if any */
  margin: marginProps,

  /** currently selected Tab, either the index number of the actual value of tab */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}; // default props

Tabs.defaultProps = {
  onChange: null,
  className: null,
  margin: null,
  componentEl: defaults,
  value: null
};
Tabs.Item = TabItem;
Tabs.Item.displayName = 'Tabs.Item';

export default Tabs;
